/**
 * Admin → Procurement Checklist (iter 91)
 *
 * Tracks the operational SaaS / vendors Nodera relies on to run the
 * platform (NOT the consumer-service vendors). Mirrors section 11 of
 * the Workflow Reference Guide.
 *
 * Features:
 *  - Summary tiles: total / live / monthly cost / BAAs signed / BAAs needed / 30d renewals
 *  - Tier filter pills (All / 0 / 1 / 2 / 3 / 4)
 *  - Table: vendor · category · tier · BAA status · adoption status · cost · renewal
 *  - Inline edit modal for any item
 *  - "Add vendor" + "Seed defaults" + "Download Workflow Guide" actions
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Clock,
  CreditCard,
  Download,
  Edit3,
  ExternalLink,
  FileDown,
  Layers,
  Loader2,
  Plus,
  RefreshCw,
  Send,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const TIER_LABELS = {
  0: "T0 · PHI (BAA required)",
  1: "T1 · Automation infra",
  2: "T2 · Healthcare glue",
  3: "T3 · Business ops",
  4: "T4 · Compliance + governance",
};
const TIER_TONES = {
  0: "bg-rose-500/15 text-rose-300 border-rose-500/40",
  1: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  2: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  3: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  4: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

const BAA_OPTIONS = [
  { key: "not_required", label: "Not required", tone: "bg-slate-700/40 text-slate-400 border-slate-700" },
  { key: "needed", label: "Needed", tone: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  { key: "in_review", label: "In review", tone: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  { key: "signed", label: "Signed", tone: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
];

const ADOPTION_OPTIONS = [
  { key: "not_started", label: "Not started", tone: "bg-slate-700/40 text-slate-400 border-slate-700" },
  { key: "evaluating", label: "Evaluating", tone: "bg-blue-500/15 text-blue-300 border-blue-500/40" },
  { key: "in_setup", label: "In setup", tone: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  { key: "live", label: "Live", tone: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  { key: "deprecated", label: "Deprecated", tone: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
];

const toneOf = (opts, key) => opts.find((o) => o.key === key)?.tone || opts[0].tone;
const labelOf = (opts, key) => opts.find((o) => o.key === key)?.label || key;

function fmtMoney(cents) {
  if (cents == null) return "—";
  return `$${(Number(cents) / 100).toFixed(2)}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}

function SummaryTile({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
      <div className={`flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold ${accent || "text-slate-500"}`}>
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-slate-100">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

function StatusPill({ tone, children, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center rounded-full border text-[10px] font-semibold uppercase px-2 py-0.5 ${tone}`}
    >
      {children}
    </span>
  );
}

function EditModal({ item, onClose, onSaved }) {
  const isNew = !item?.id;
  const [form, setForm] = useState({
    vendor_name: item?.vendor_name || "",
    category: item?.category || "",
    tier: item?.tier ?? 2,
    needs_baa: !!item?.needs_baa,
    baa_status: item?.baa_status || "not_required",
    adoption_status: item?.adoption_status || "not_started",
    monthly_cost_cents: item?.monthly_cost_cents ?? "",
    renewal_at: item?.renewal_at ? item.renewal_at.slice(0, 10) : "",
    account_owner: item?.account_owner || "",
    url: item?.url || "",
    notes: item?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    if (!form.vendor_name.trim()) {
      toast.error("Vendor name is required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        vendor_name: form.vendor_name.trim(),
        category: form.category.trim() || null,
        tier: Number(form.tier),
        needs_baa: !!form.needs_baa,
        baa_status: form.baa_status,
        adoption_status: form.adoption_status,
        monthly_cost_cents: form.monthly_cost_cents === "" ? null : Number(form.monthly_cost_cents),
        renewal_at: form.renewal_at ? new Date(form.renewal_at).toISOString() : null,
        account_owner: form.account_owner.trim() || null,
        url: form.url.trim() || null,
        notes: form.notes.trim() || null,
      };
      if (isNew) {
        await api.post("/admin/procurement", body);
        toast.success("Vendor added.");
      } else {
        await api.patch(`/admin/procurement/${item.id}`, body);
        toast.success("Vendor updated.");
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur flex items-center justify-center px-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="procurement-edit-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <h3 className="text-sm font-bold text-slate-100">{isNew ? "Add operational vendor" : "Edit vendor"}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="procurement-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase font-semibold text-slate-500">Vendor name *</span>
            <input value={form.vendor_name} onChange={set("vendor_name")} data-testid="procurement-form-name" className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-slate-100" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-semibold text-slate-500">Category</span>
            <input value={form.category} onChange={set("category")} data-testid="procurement-form-category" placeholder="e.g. E-signature" className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-slate-100" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-semibold text-slate-500">Tier</span>
            <select value={form.tier} onChange={set("tier")} data-testid="procurement-form-tier" className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-slate-100">
              {Object.entries(TIER_LABELS).map(([k, label]) => (<option key={k} value={k}>{label}</option>))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-semibold text-slate-500">BAA status</span>
            <select value={form.baa_status} onChange={set("baa_status")} data-testid="procurement-form-baa" className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-slate-100">
              {BAA_OPTIONS.map((o) => (<option key={o.key} value={o.key}>{o.label}</option>))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-semibold text-slate-500">Adoption status</span>
            <select value={form.adoption_status} onChange={set("adoption_status")} data-testid="procurement-form-adoption" className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-slate-100">
              {ADOPTION_OPTIONS.map((o) => (<option key={o.key} value={o.key}>{o.label}</option>))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-semibold text-slate-500">Monthly cost (cents)</span>
            <input value={form.monthly_cost_cents} onChange={set("monthly_cost_cents")} type="number" min={0} data-testid="procurement-form-cost" placeholder="e.g. 19900 = $199" className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-slate-100" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-semibold text-slate-500">Renewal date</span>
            <input value={form.renewal_at} onChange={set("renewal_at")} type="date" data-testid="procurement-form-renewal" className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-slate-100" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase font-semibold text-slate-500">Account owner</span>
            <input value={form.account_owner} onChange={set("account_owner")} data-testid="procurement-form-owner" placeholder="e.g. Kyle" className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-slate-100" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase font-semibold text-slate-500">URL</span>
            <input value={form.url} onChange={set("url")} data-testid="procurement-form-url" placeholder="https://" className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-slate-100" />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[10px] uppercase font-semibold text-slate-500">Notes</span>
            <textarea value={form.notes} onChange={set("notes")} rows={3} data-testid="procurement-form-notes" className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-slate-100" />
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-300 sm:col-span-2">
            <input type="checkbox" checked={form.needs_baa} onChange={(e) => setForm((f) => ({ ...f, needs_baa: e.target.checked }))} data-testid="procurement-form-needs-baa" className="accent-blue-500" />
            Requires a signed BAA
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-800">
          <button onClick={onClose} className="text-xs font-semibold text-slate-400 hover:text-slate-200 px-3 py-1.5">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="procurement-form-save" className="inline-flex items-center gap-1 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-4 py-1.5 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminProcurement() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/procurement"),
        api.get("/admin/procurement/summary"),
      ]);
      setItems(r1.data?.items || []);
      setSummary(r2.data || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => { await load(); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/admin/procurement/seed", {});
      toast.success(`Seeded ${r.data.inserted} new, skipped ${r.data.skipped_existing} existing.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSeeding(false);
    }
  };

  const [sendingDigest, setSendingDigest] = useState(false);
  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const r = await api.post("/admin/procurement/digest/send-now", {});
      const p = r.data?.payload || {};
      const sent = r.data?.send_result?.sent;
      toast.success(
        sent
          ? `Digest emailed: ${p.upcoming_renewals?.length || 0} renewal(s), ${p.baa_pending?.length || 0} BAA pending.`
          : `Digest computed but email send failed: ${r.data?.send_result?.error || "unknown error"}`
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingDigest(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this vendor from your procurement list?")) return;
    try {
      await api.delete(`/admin/procurement/${id}`);
      toast.success("Removed.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = useMemo(() => {
    if (tierFilter === "all") return items;
    return items.filter((i) => String(i.tier) === String(tierFilter));
  }, [items, tierFilter]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-[1400px] mx-auto px-6 py-8">

        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Admin · Integrations</div>
            <h1 className="mt-1 text-3xl font-bold flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-emerald-400" />
              Procurement Checklist
            </h1>
            <p className="mt-2 text-sm text-slate-400 max-w-3xl">
              Track the operational SaaS vendors Nodera relies on to run the platform — adoption status,
              BAA signatures, monthly cost, and renewal dates. See section 11 of the Workflow Reference Guide
              for the full rationale per vendor.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href="/api/static/downloads/Nodera-Workflow-Reference-Guide.pdf"
              target="_blank" rel="noreferrer"
              data-testid="procurement-download-guide"
              className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold px-3 py-1.5 text-slate-200 hover:bg-slate-700"
            >
              <FileDown className="w-3.5 h-3.5" /> Workflow Guide PDF
            </a>
            <button
              type="button" onClick={load} disabled={loading}
              data-testid="procurement-refresh"
              className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold px-3 py-1.5 text-slate-200 hover:bg-slate-700 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
            <button
              type="button" onClick={seed} disabled={seeding}
              data-testid="procurement-seed"
              className="inline-flex items-center gap-1 rounded-full bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
            >
              {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Seed defaults
            </button>
            <button
              type="button" onClick={sendDigest} disabled={sendingDigest}
              data-testid="procurement-send-digest"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
              title="Manually fire the monthly digest email to your inbox"
            >
              {sendingDigest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send digest now
            </button>
            <button
              type="button" onClick={() => setEditing({})}
              data-testid="procurement-new"
              className="inline-flex items-center gap-1 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-3 py-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> Add vendor
            </button>
          </div>
        </div>

        {/* Summary tiles */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="procurement-summary">
            <SummaryTile icon={Layers} label="Vendors tracked" value={summary.total} testid="procurement-stat-total" />
            <SummaryTile icon={CheckCircle2} label="Live" value={summary.live} accent="text-emerald-400" testid="procurement-stat-live" />
            <SummaryTile icon={CreditCard} label="Monthly cost (live)" value={fmtMoney(summary.monthly_cost_cents)} testid="procurement-stat-cost" />
            <SummaryTile icon={ShieldCheck} label="BAAs signed" value={summary.baa_signed} sub={`${summary.baa_needed} pending`} accent="text-emerald-400" testid="procurement-stat-baa" />
            <SummaryTile icon={Clock} label="Renewals in 30d" value={summary.upcoming_renewals_30d?.length || 0} accent="text-amber-400" testid="procurement-stat-renewals" />
          </div>
        )}

        {/* Tier filter */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button
            onClick={() => setTierFilter("all")}
            data-testid="procurement-filter-all"
            className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${tierFilter === "all" ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"}`}
          >
            All ({items.length})
          </button>
          {[0, 1, 2, 3, 4].map((t) => (
            <button
              key={t} onClick={() => setTierFilter(String(t))}
              data-testid={`procurement-filter-tier-${t}`}
              className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${String(tierFilter) === String(t) ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800"}`}
            >
              {TIER_LABELS[t]} ({summary?.by_tier?.[t] || 0})
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="procurement-table">
              <thead className="bg-slate-900/70 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Vendor</th>
                  <th className="text-left px-4 py-2.5">Category</th>
                  <th className="text-left px-4 py-2.5">Tier</th>
                  <th className="text-left px-4 py-2.5">BAA</th>
                  <th className="text-left px-4 py-2.5">Adoption</th>
                  <th className="text-right px-4 py-2.5">Monthly</th>
                  <th className="text-right px-4 py-2.5">Renewal</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-slate-500 text-sm py-8">
                    {loading ? "Loading…" : 'No vendors. Click "Seed defaults" to populate from the Workflow Guide.'}
                  </td></tr>
                ) : filtered.map((r) => (
                  <tr key={r.id} data-testid={`procurement-row-${r.id}`} className="hover:bg-slate-900/40">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-100 flex items-center gap-1.5">
                        {r.vendor_name}
                        {r.url && (
                          <a href={r.url} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-blue-400" aria-label="Open vendor URL">
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      {r.account_owner && (<div className="text-[11px] text-slate-500">Owner: {r.account_owner}</div>)}
                    </td>
                    <td className="px-4 py-3 text-slate-300 text-xs">{r.category || "—"}</td>
                    <td className="px-4 py-3">
                      <StatusPill tone={TIER_TONES[r.tier] || TIER_TONES[2]} testid={`procurement-tier-${r.id}`}>
                        T{r.tier}
                      </StatusPill>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill tone={toneOf(BAA_OPTIONS, r.baa_status)} testid={`procurement-baa-${r.id}`}>
                        {labelOf(BAA_OPTIONS, r.baa_status)}
                      </StatusPill>
                      {r.needs_baa && r.baa_status !== "signed" && (
                        <div className="text-[10px] text-rose-300 mt-1">Required</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill tone={toneOf(ADOPTION_OPTIONS, r.adoption_status)} testid={`procurement-adoption-${r.id}`}>
                        {labelOf(ADOPTION_OPTIONS, r.adoption_status)}
                      </StatusPill>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-200 text-xs">{fmtMoney(r.monthly_cost_cents)}</td>
                    <td className="px-4 py-3 text-right text-xs text-slate-300">{fmtDate(r.renewal_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => setEditing(r)} data-testid={`procurement-edit-${r.id}`} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-blue-300" aria-label="Edit">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(r.id)} data-testid={`procurement-delete-${r.id}`} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-rose-300" aria-label="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes preview for selected items (top-of-tier-0 critical-path reminder) */}
        {summary?.baa_needed > 0 && (
          <div className="mt-6 text-[11px] text-amber-300 flex items-start gap-2 max-w-2xl">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>
              <span className="font-bold">{summary.baa_needed} vendor{summary.baa_needed === 1 ? "" : "s"}</span>{" "}
              touch PHI and need a signed BAA before going live. Sort by Tier 0
              and prioritize signing those before adopting anything else.
            </span>
          </div>
        )}

      </div>

      {editing && (
        <EditModal item={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}
